
import { PVP_FREE, PVP_GUILD, PVP_PEACE, PVP_REVENGE } from "./character";
import { elem, styleInstaller } from "./chrome";
import {
  browserStorage,
  loadClientOptions,
  saveClientOptions,
  type ClientOptions,
  type NameColorMode,
} from "./systemoptions";

export function isNameColorMode(value: unknown): value is NameColorMode {
  return value === "normal" || value === "empire";
}

let liveNameColorMode: NameColorMode = "normal";

let liveNameColorEmpire = 1;

export function getNameColorMode(): NameColorMode {
  return liveNameColorMode;
}

export function setNameColorMode(mode: NameColorMode): void {
  liveNameColorMode = mode;
}

export function getNameColorEmpire(): number {
  return liveNameColorEmpire;
}

export function setNameColorEmpire(empire: number): void {
  if (empire === 1 || empire === 2 || empire === 3) liveNameColorEmpire = empire;
}

/** `colorinfo.py` `CHR_NAME_RGB_EMPIRE_PC_A/B/C` as CSS hex, index 1..3. */
export const EMPIRE_PC_NAME_COLOURS: readonly string[] = [
  "",
  "#9d0000",
  "#dea02f",
  "#171e8a",
];

export function empirePcNameColour(empire: number): string {
  if (empire === 1 || empire === 2 || empire === 3) {
    return EMPIRE_PC_NAME_COLOURS[empire];
  }
  return EMPIRE_PC_NAME_COLOURS[1];
}

export type LocalOption = "targetBoardVisible" | "alwaysShowNames" | "showDamage" | "showSalesText" | "viewChat";

export const PVP_OPTION_MODES: readonly number[] = [PVP_PEACE, PVP_REVENGE, PVP_GUILD, PVP_FREE];

export interface GameOptionsActions {
  /** Apply a presentation choice immediately; never send it to the server. */
  readonly onLocalOption?: (name: LocalOption, value: boolean) => void;
  /** Apply the local name-colour mode immediately; never send it to the server. */
  readonly onNameColorMode?: (mode: NameColorMode) => void;
  /** Ask the server for a PvP mode. The argument is a `PvpMode` wire value --
   *  see `PVP_OPTION_MODES`. Only ever a request; call `updatePvpMode` with
   *  whatever the server actually confirms. */
  readonly onPvpMode?: (mode: number) => void;
  /** All six `SetBlockMode` flags, always sent together -- see the message's
   *  own doc for why: the server overwrites the whole byte in one call. */
  readonly onBlockMode?: (
    whisper: boolean,
    messengerInvite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
    partyRequest: boolean,
  ) => void;
  /** Keep the standalone cog for builds without the enclosing system menu. */
  readonly showTrigger?: boolean;
}

const STYLE_ID = "gameoptions-style";
const STYLE = `
.options-open {
  position: fixed; right: max(12px, env(safe-area-inset-right));
  top: max(12px, env(safe-area-inset-top)); z-index: 70;
  width: 40px; height: 36px; border: 1px solid #8d7044; border-radius: 4px;
  color: #ead7ae; background: linear-gradient(#3b3023, #18140f);
  box-shadow: inset 0 0 0 1px rgba(255,220,160,.12), 0 3px 12px #0009;
  pointer-events: auto; cursor: pointer; font: 18px/1 serif;
}
.options-panel {
  position: fixed; right: max(12px, env(safe-area-inset-right));
  top: max(58px, calc(env(safe-area-inset-top) + 58px)); z-index: 69;
  width: min(328px, calc(100vw - 24px)); max-height: calc(100vh - 76px);
  overflow: auto; padding: 9px; pointer-events: auto;
  border: 1px solid #8d7044; border-radius: 5px; color: #eadfca;
  background: linear-gradient(145deg, rgba(48,39,28,.97), rgba(15,13,11,.97));
  box-shadow: inset 0 0 0 1px rgba(255,220,160,.1), 0 12px 32px #000b;
  font: 12px/1.35 system-ui, sans-serif;
}
.options-title { display:flex; align-items:center; justify-content:space-between;
  padding: 2px 3px 8px; color:#f1d69e; text-transform:uppercase;
  letter-spacing:.1em; font-weight:700; }
.options-close { border:0; color:#d8c7a6; background:transparent; cursor:pointer;
  font:18px/1 serif; }
.options-group { margin:0 0 8px; padding:9px; border:1px solid rgba(184,145,83,.35);
  background:rgba(0,0,0,.18); }
.options-group legend { padding:0 5px; color:#c7a66c; text-transform:uppercase;
  letter-spacing:.08em; font-size:10px; }
.options-row { display:grid; grid-template-columns: 88px 1fr 34px; align-items:center;
  gap:8px; margin:5px 0; }
.options-select { width:100%; padding:4px 6px; border:1px solid #735b36;
  border-radius:3px; color:#eadfca; background:#201a13; }
.options-note { margin:5px 0 0; color:#9f927d; font-size:10px; }
.options-check { display:block; margin:6px 0; cursor:pointer; }
.options-check input { accent-color:#b99355; margin-right:7px; }
.options-toggle { display:inline-flex; align-items:center; gap:5px; margin:3px 12px 3px 0; cursor:pointer; }
.options-toggle input { accent-color:#b99355; }
.options-radio { display:inline-flex; align-items:center; gap:4px; margin:2px 12px 2px 0; cursor:pointer; }
.options-radio input { accent-color:#b99355; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

/** Wire value, display label, `_TOOLTIP` string (`locale_interface.txt:236-244`). */
const PVP_ROWS: readonly (readonly [mode: number, label: string, tooltip: string])[] = [
  [PVP_PEACE, "Peace", "You cannot attack players from your kingdom."],
  [PVP_REVENGE, "Hostile", "You can only attack those who have another Ethos."],
  [PVP_GUILD, "Guild", "You are allowed to attack all players but your Guild members."],
  [PVP_FREE, "Free", "You are allowed to attack all players."],
];

/** `blockButtonList` order (`uigameoption.py:70-75`): exchange, party, guild,
 *  whisper, friend, party_request -- two rows of three, y115 then y140. */
const BLOCK_ROWS: readonly (readonly [option: string, label: string])[] = [
  ["exchange", "Trade"],
  ["party-invite", "Group"],
  ["guild-invite", "Guild"],
  ["whisper", "Whisper"],
  ["messenger-invite", "Friends"],
  ["party-request", "Request"],
];

/** `[data-option slug, ClientOptions field, "on" caption]`, in the script's
 *  own y-order. Slugs and the checkbox shape are `ui/options.ts`'s own
 *  (`tools/e2e/options.mjs` drives them by these exact names) -- see the
 *  module doc for why this split keeps that contract. */
const LOCAL_CHECK_ROWS: readonly (readonly [slug: string, field: LocalOption, onCaption: string])[] = [
  ["target-board", "targetBoardVisible", "Show"],
  ["chat", "viewChat", "Show"],
  ["names", "alwaysShowNames", "Always"],
  ["damage", "showDamage", "Show"],
  ["sales", "showSalesText", "Show"],
];

export class GameOptionsPanel {
  private readonly root: HTMLElement;
  private readonly storage: Storage | null;
  private value: ClientOptions;
  private pvpMode = PVP_PEACE;
  private block = {
    whisper: false, messengerInvite: false, exchange: false,
    partyInvite: false, guildInvite: false, partyRequest: false,
  };

  constructor(
    mount: HTMLElement,
    private readonly actions: GameOptionsActions,
    storage: Storage | null = browserStorage(),
    initial: ClientOptions = loadClientOptions(storage),
  ) {
    const doc = mount.ownerDocument;
    installStyle(doc);
    this.storage = storage;
    this.value = { ...initial };
    liveNameColorMode = initial.nameColorMode;

    const open = doc.createElement("button");
    open.type = "button";
    open.className = "options-open";
    open.title = "Game options";
    open.setAttribute("aria-label", "Open game options");
    open.textContent = "⚔";

    this.root = elem(doc, "section", "options-panel");
    this.root.setAttribute("aria-label", "Game options");
    this.root.hidden = true;
    this.root.innerHTML = `
      <header class="options-title"><span>Game options</span><button type="button" class="options-close" aria-label="Close game options">×</button></header>
      <fieldset class="options-group"><legend>Name Color</legend>
        <label class="options-row"><span>Nameplates</span><select class="options-select" data-option="name-color"><option value="normal">Normal</option><option value="empire">Kingdom Color</option></select><output></output></label>
      </fieldset>
      <fieldset class="options-group"><legend>Target Menu</legend>
        <label class="options-check"><input type="checkbox" data-option="target-board"> Show</label>
      </fieldset>
      <fieldset class="options-group"><legend>PvP Mode</legend>${this.pvpHtml()}</fieldset>
      <fieldset class="options-group"><legend>Block</legend>${this.blockHtml()}
      </fieldset>
      <fieldset class="options-group"><legend>Chat Line</legend>
        <label class="options-check"><input type="checkbox" data-option="chat"> Show</label>
      </fieldset>
      <fieldset class="options-group"><legend>Name</legend>
        <label class="options-check"><input type="checkbox" data-option="names"> Always</label>
      </fieldset>
      <fieldset class="options-group"><legend>Hit Info</legend>
        <label class="options-check"><input type="checkbox" data-option="damage"> Show</label>
      </fieldset>
      <fieldset class="options-group"><legend>Shop name</legend>
        <label class="options-check"><input type="checkbox" data-option="sales"> Show</label>
      </fieldset>`;

    open.addEventListener("click", () => this.setVisible(!this.showing));
    this.root.querySelector(".options-close")?.addEventListener("click", () => this.setVisible(false));

    // `uigameoption.py:151-153` / `constInfo.SET_CHRNAME_COLOR_INDEX`: Normal
    // (0) or Kingdom Color (1). Local only -- `chrmgr.SetEmpireNameMode`,
    // never a GC/CG.
    this.root.querySelector<HTMLSelectElement>('[data-option="name-color"]')?.addEventListener("change", (event) => {
      const mode = (event.currentTarget as HTMLSelectElement).value;
      if (!isNameColorMode(mode)) return;
      this.value = { ...this.value, nameColorMode: mode };
      liveNameColorMode = mode;
      this.actions.onNameColorMode?.(mode);
      this.persist();
    });

    for (const [slug, field] of LOCAL_CHECK_ROWS) {
      this.root.querySelector<HTMLInputElement>(`[data-option="${slug}"]`)?.addEventListener("change", (event) => {
        const enabled = (event.currentTarget as HTMLInputElement).checked;
        this.value = { ...this.value, [field]: enabled };
        this.actions.onLocalOption?.(field, enabled);
        this.persist();
      });
    }

    for (const radio of this.root.querySelectorAll<HTMLInputElement>('[data-option="pvp"]')) {
      radio.addEventListener("change", (event) => {
        const requested = Number((event.currentTarget as HTMLInputElement).value);
        this.refreshPvp();
        this.actions.onPvpMode?.(requested);
      });
    }

    for (const [option] of BLOCK_ROWS) {
      this.root.querySelector<HTMLInputElement>(`[data-option="block-${option}"]`)?.addEventListener("change", (event) => {
        const checked = (event.currentTarget as HTMLInputElement).checked;
        this.setBlockFlag(option, checked);
        this.actions.onBlockMode?.(
          this.block.whisper, this.block.messengerInvite, this.block.exchange,
          this.block.partyInvite, this.block.guildInvite, this.block.partyRequest,
        );
      });
    }

    // `GameOptionDialog.OnPressEscapeKey` closes the option window and
    // consumes Escape (`uigameoption.py:328-330`).
    const onEscape = (event: KeyboardEvent) => {
      if (!this.showing || event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      this.setVisible(false);
      open.focus();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });
    if (actions.showTrigger !== false) mount.append(open);
    mount.append(this.root);
    this.refresh();
    this.refreshPvp();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get options(): ClientOptions {
    return { ...this.value };
  }

  setVisible(visible: boolean): void {
    // Re-read on every open -- see `systemoptions.ts`'s `SystemOptionsPanel`,
    // which shares the same reasoning and the same persisted blob.
    if (visible) {
      this.value = loadClientOptions(this.storage);
      this.refresh();
    }
    this.root.hidden = !visible;
  }

  /** Draw the mode the server actually confirmed (`PvpStatus`, sent on join
   *  and after every `SetPvpMode`). Wire this to `session.onPvpMode`. */
  updatePvpMode(mode: number): void {
    this.pvpMode = mode;
    this.refreshPvp();
  }

  private setBlockFlag(option: string, checked: boolean): void {
    switch (option) {
      case "exchange": this.block.exchange = checked; break;
      case "party-invite": this.block.partyInvite = checked; break;
      case "guild-invite": this.block.guildInvite = checked; break;
      case "whisper": this.block.whisper = checked; break;
      case "messenger-invite": this.block.messengerInvite = checked; break;
      case "party-request": this.block.partyRequest = checked; break;
      default: break;
    }
  }

  private pvpHtml(): string {
    return PVP_ROWS.map(([mode, label, tooltip]) =>
      `<label class="options-radio" title="${tooltip}"><input type="radio" name="gameoptions-pvp" data-option="pvp" value="${mode}"> ${label}</label>`,
    ).join("");
  }

  private blockHtml(): string {
    return BLOCK_ROWS.map(([option, label]) =>
      `<label class="options-toggle"><input type="checkbox" data-option="block-${option}"> ${label}</label>`,
    ).join("");
  }

  private refresh(): void {
    const nameColor = this.root.querySelector<HTMLSelectElement>('[data-option="name-color"]');
    if (nameColor !== null) nameColor.value = this.value.nameColorMode;
    for (const [slug, field] of LOCAL_CHECK_ROWS) {
      const control = this.root.querySelector<HTMLInputElement>(`[data-option="${slug}"]`);
      if (control !== null) control.checked = this.value[field];
    }
  }

  private refreshPvp(): void {
    for (const radio of this.root.querySelectorAll<HTMLInputElement>('[data-option="pvp"]')) {
      radio.checked = Number(radio.value) === this.pvpMode;
    }
  }

  private persist(): void {
    // Overlay only this panel's own fields onto the latest persisted blob --
    // see `systemoptions.ts`'s `SystemOptionsPanel.persist` for why.
    const latest = loadClientOptions(this.storage);
    const merged: ClientOptions = {
      ...latest,
      targetBoardVisible: this.value.targetBoardVisible,
      alwaysShowNames: this.value.alwaysShowNames,
      showDamage: this.value.showDamage,
      showSalesText: this.value.showSalesText,
      viewChat: this.value.viewChat,
      nameColorMode: this.value.nameColorMode,
    };
    this.value = merged;
    saveClientOptions(merged, this.storage);
  }
}
